/**
 * Authentication and session utilities for chat feature
 * @remarks
 * These utilities manage user identification and session ID generation for chat.
 * They support both authenticated users and unauthenticated fallback users.
 * Uses platform-aware storage (localStorage on web, AsyncStorage on native).
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";

import type { AuthUserFragment } from "@/generated/graphql";
import { generateUUID } from "@/utils/uuid";

import type { QueryRequest } from "../types";

/**
 * Storage key for chat user ID with namespace prefix
 */
const STORAGE_KEY = "@gemini-sports-ai:chat-user-id";

/**
 * Get or create persistent userId (fallback for unauthenticated users)
 * @remarks Uses platform-aware storage: localStorage on web, AsyncStorage on native
 * @returns User ID from storage or newly generated UUID
 */
export const getOrCreateFallbackUserId = async (): Promise<string> => {
  try {
    const stored =
      Platform.OS === "web"
        ? localStorage.getItem(STORAGE_KEY)
        : await AsyncStorage.getItem(STORAGE_KEY);
    if (stored) {
      return stored;
    }
  } catch {
    // Storage might not be available in some environments
  }

  const newUserId = generateUUID();

  try {
    if (Platform.OS === "web") {
      localStorage.setItem(STORAGE_KEY, newUserId);
    } else {
      await AsyncStorage.setItem(STORAGE_KEY, newUserId);
    }
  } catch {
    // Ignore storage errors
  }

  return newUserId;
};

/**
 * Get userId with auth integration and fallback
 * @remarks Returns async Promise to support platform-aware storage
 * @param currentUser - Current authenticated user or null/undefined
 * @returns User ID from authenticated user or fallback UUID
 */
export const getUserId = async (
  currentUser: AuthUserFragment | null | undefined
): Promise<string> => {
  // Use authenticated user ID if available
  if (currentUser?.id) {
    return currentUser.id;
  }

  // Fallback to platform-aware storage UUID for unauthenticated users
  return getOrCreateFallbackUserId();
};

/**
 * Get organization ID from the authenticated user.
 *
 * SE-5426/SE-5427: returns an EMPTY string when the user (or their org) has not
 * resolved yet — it must NEVER fall back to a default/placeholder org. Ask Gemini
 * is authenticated-only; sending a request whose org the user does not belong to
 * makes the agent return 401 "Invalid session". Callers gate sending on a
 * non-empty org, so an empty value here keeps the chat from querying a foreign org.
 * @param currentUser - Current authenticated user or null/undefined
 * @returns The user's organization ID, or "" when it has not resolved
 */
export const getOrganizationId = (
  currentUser: AuthUserFragment | null | undefined
): string => {
  return currentUser?.organizationId || "";
};

/**
 * Generate sessionId in format: organizationId#userId#uuid
 *
 * SE-5426/SE-5427: never injects a default org. When `organizationId` is empty
 * the org segment is left empty; callers gate sending until the real org is known,
 * so a session is only ever dispatched with the user's actual organization.
 * @param organizationId - The user's organization ID (empty until resolved)
 * @param userId - User ID
 * @returns Session ID string in format organizationId#userId#uuid
 */
export const generateSessionId = (
  organizationId: string | undefined,
  userId: string
): string => {
  return `${organizationId || ""}#${userId}#${generateUUID()}`;
};

/**
 * Resolves the sessionId to dispatch for a chat send, guaranteeing its
 * organization prefix matches the resolved org.
 *
 * SE-5451: the main chat send path historically reused the stored `sessionIdVar`
 * verbatim. On a cold load `updateAuthContext(undefined)` mints that session as
 * `#<fallbackUserId>#<uuid>` (empty org segment + fallback user) before the `me`
 * query resolves; dispatching it makes the agent return 401 "Invalid session"
 * even though the request's `organizationId` field is already correct — the
 * small-screen/native recurrence of SE-5426/SE-5427.
 *
 * - An existing server thread id (`selectedThreadId`) is used verbatim — it is a
 *   real conversation id, not an `org#user#uuid` session, and must not be rewritten.
 * - Otherwise a stored session is reused only when its org prefix already matches
 *   the resolved org (keeps the conversation stable across sends), and is
 *   regenerated from the resolved org/user when it is empty, stale, or foreign.
 * @param params - Session-resolution inputs
 * @param params.selectedThreadId - Selected server thread id, if a thread is open
 * @param params.storedSessionId - The currently stored `org#user#uuid` session, if any
 * @param params.organizationId - The resolved organization id (empty until known)
 * @param params.userId - The resolved user id
 * @returns The sessionId to dispatch, always org-correct for a new conversation
 */
export const resolveDispatchSessionId = (params: {
  selectedThreadId: string | null | undefined;
  storedSessionId: string | null | undefined;
  organizationId: string;
  userId: string;
}): string => {
  const { selectedThreadId, storedSessionId, organizationId, userId } = params;

  if (selectedThreadId) {
    return selectedThreadId;
  }

  const storedOrgPrefix = storedSessionId ? storedSessionId.split("#")[0] : "";
  if (storedSessionId && organizationId && storedOrgPrefix === organizationId) {
    return storedSessionId;
  }

  return generateSessionId(organizationId, userId);
};

/**
 * The org/user/session fields every authenticated Ask Gemini request must carry.
 */
export interface AgentRequestBase {
  readonly sessionId: string;
  readonly tenantId: string;
  readonly organizationId: string;
  readonly userId: string;
}

/**
 * Builds the org/user/session base of an Ask Gemini agent request, or returns
 * null when the authenticated user's organization has not resolved yet.
 *
 * SE-5426/SE-5427: Ask Gemini is authenticated-only and must NEVER query a
 * default/placeholder org (the agent returns 401 "Invalid session"). Callers
 * spread the returned base into their request and return early on null, so a
 * request is only ever dispatched with the user's real organization.
 * @param currentUser - Current authenticated user or null/undefined
 * @returns The request base fields, or null when the org is unknown
 */
export const buildAgentRequestBase = (
  currentUser: AuthUserFragment | null | undefined
): AgentRequestBase | null => {
  const organizationId = getOrganizationId(currentUser);
  if (!organizationId) {
    return null;
  }

  const userId = currentUser?.id || "";
  return {
    sessionId: generateSessionId(organizationId, userId),
    tenantId: organizationId,
    organizationId,
    userId,
  };
};

/**
 * Builds the streaming player-summary {@link QueryRequest}, or null when the
 * authenticated user's organization has not resolved yet.
 *
 * SE-5426/SE-5427: shared by the `useAISummary` hook and the
 * `AIPlayerSummaryContainer` component so the player-summary request shape (and
 * the authenticated-only org rule) is defined once rather than duplicated. A
 * null return means the caller must NOT dispatch (the agent rejects a
 * default/foreign org with 401 "Invalid session").
 *
 * SE-8432: the request is sent in `completion` mode. The summary prompt is a
 * complete instruction, not a chat question, so it must bypass Ask Gemini
 * v2's intent classifier and agent loop and come back as a plain completion.
 * @param currentUser - Current authenticated user or null/undefined
 * @param prompt - The generated summary prompt
 * @param playerId - The player the summary is for
 * @param accessToken - The auth access token, if available
 * @returns The request to send, or null when the org is unresolved
 */
export const buildPlayerSummaryRequest = (
  currentUser: AuthUserFragment | null | undefined,
  prompt: string,
  playerId: string,
  accessToken: string | null | undefined
): QueryRequest | null => {
  const base = buildAgentRequestBase(currentUser);
  if (!base) {
    return null;
  }

  return {
    question: prompt,
    ...base,
    playerId,
    isStreaming: true,
    mode: "completion",
    ...(accessToken && { accessKey: accessToken }),
  };
};
